import { useQuery } from "react-query"
import {
  getMyTicketsForPost,
  getTicketPurchase,
  type TicketPurchaseResponse,
  type TicketPurchasesResponse,
} from "../requests/ticketPurchases"

export interface UseTicketPurchaseOptions {
  enabled?: boolean
  refetchInterval?: number
  staleTime?: number
  cacheTime?: number
}

const MY_TICKETS_CACHE_KEY = "MY_TICKETS"

/**
 * Hook to get the current user's ticket purchases for a specific post
 *
 * @param postId - The ID of the post to get the user's tickets for
 * @param options - React Query options
 * @returns Query result with the user's ticket purchases array
 */
export function useMyTicketsForPost(
  postId: number | null,
  options: UseTicketPurchaseOptions = {},
) {
  const { enabled = true, refetchInterval, staleTime, cacheTime } = options

  return useQuery<TicketPurchasesResponse, Error>(
    [MY_TICKETS_CACHE_KEY, postId],
    () => getMyTicketsForPost(postId!),
    {
      enabled: enabled && postId !== null,
      refetchInterval,
      staleTime: staleTime || 2 * 60 * 1000, // 2 minutes
      cacheTime: cacheTime || 5 * 60 * 1000, // 5 minutes
      retry: 1,
      retryDelay: 1000,
      refetchOnWindowFocus: false, // Prevent refetch on window focus to reduce spam
      refetchOnReconnect: false, // Prevent refetch on reconnect to reduce spam
    },
  )
}

/**
 * Hook to get a specific ticket purchase by ID
 *
 * @param postId - The ID of the post the ticket belongs to
 * @param ticketPurchaseId - The ID of the ticket purchase
 * @param options - React Query options
 * @returns Query result with the specific ticket purchase
 */
export function useTicketPurchaseById(
  postId: number | null,
  ticketPurchaseId: number | null,
  options: UseTicketPurchaseOptions = {},
) {
  const { enabled = true, refetchInterval, staleTime, cacheTime } = options

  return useQuery<TicketPurchaseResponse, Error>(
    ["ticketPurchase", postId, ticketPurchaseId],
    () => getTicketPurchase(postId!, ticketPurchaseId!),
    {
      enabled: enabled && postId !== null && ticketPurchaseId !== null,
      refetchInterval,
      staleTime: staleTime || 5 * 60 * 1000, // 5 minutes
      cacheTime: cacheTime || 10 * 60 * 1000, // 10 minutes
      retry: 1,
      retryDelay: 1000,
    },
  )
}

// Query key factory for manual cache management
export const ticketPurchaseQueryKeys = {
  myTickets: (postId: number) => [MY_TICKETS_CACHE_KEY, postId] as const,
  byId: (postId: number, ticketPurchaseId: number) =>
    ["ticketPurchase", postId, ticketPurchaseId] as const,
}
